import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";

import useAdventCampaign from "apps/user/learner/hooks/use-advent-campaign";
import {
  CATEGORY_ICONS,
  daysToStart,
  formatDoorDate,
  isBeforeStart,
  localToday,
  todaysDoor,
} from "apps/user/learner/lib/advent-doors";
import { ORI_ORB_CROP_SCALE, OriAvatar } from "components/ori";
import { useTranslation } from "lib/i18n/use-translation";
import { trackEvent } from "utility/mixpanel";
import { useInViews } from "utility/useInViews";

import styles from "./december-doors-band.module.scss";

const CALENDAR_ROUTE = "/user/december-doors";

// For the element Home lays out directly after the band, so it tucks up under
// the band's fade and the sky dissolves into it.
export const TUCKED_UNDER_BAND_CLASS = styles["tucked-under"];

// Flag-gated home entry point for the door calendar: a full-width night-sky
// band at the top of the page. Renders nothing until there is a campaign to
// show. The analytics keep the names mobile shares.
const DecemberDoorsBand = () => {
  const { t, i18n } = useTranslation(["decemberDoors"]);
  const navigate = useNavigate();
  const { campaign, error } = useAdventCampaign();
  const { ref, inView } = useInViews();
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false);

  const today = localToday();
  const doorForToday = campaign ? todaysDoor(campaign.doors, today) : undefined;
  // The week before the first door the band counts down to it instead.
  const isCountdown = campaign ? isBeforeStart(campaign, today) : false;
  // The day the band advertises; null once today's door is opened and the
  // band shows the counter instead.
  const readyDay =
    doorForToday && !doorForToday.isOpened ? doorForToday.dayNumber : null;

  // One impression per mount, the first time the band is actually seen.
  useEffect(() => {
    if (!campaign || !inView || hasTrackedImpression) return;
    setHasTrackedImpression(true);
    trackEvent("December Doors Card Impression", {
      Campaign: campaign.slug,
      "Door Day": readyDay,
      "Opened Count": campaign.openedCount,
    });
  }, [campaign, inView, hasTrackedImpression, readyDay]);

  if (error || !campaign) return null;

  const title = () => {
    if (readyDay !== null) {
      return t("decemberDoors:card.doorReady", { day: readyDay });
    }
    if (doorForToday) {
      return t("decemberDoors:card.doorOpened", {
        day: doorForToday.dayNumber,
      });
    }
    return campaign.name;
  };

  const handleClick = () => {
    trackEvent("December Doors Card Tapped", {
      Campaign: campaign.slug,
      "Door Day": readyDay,
      "Opened Count": campaign.openedCount,
    });
    navigate(
      readyDay === null ? CALENDAR_ROUTE : `${CALENDAR_ROUTE}?door=${readyDay}`
    );
  };

  const status = () => {
    if (isCountdown) {
      return (
        <>
          {t("decemberDoors:empty.beforeStart", {
            date: formatDoorDate(campaign.startDate, i18n.language),
          })}
          <span className={styles["status-detail"]}>
            {t("decemberDoors:empty.daysToGo", {
              count: daysToStart(campaign, today),
            })}
          </span>
        </>
      );
    }
    if (campaign.openedCount === 0) return t("decemberDoors:counter.none");
    return t("decemberDoors:card.openedCount", { count: campaign.openedCount });
  };

  const Icon = doorForToday ? CATEGORY_ICONS[doorForToday.category] : null;
  const isDoorOpened = Boolean(doorForToday?.isOpened);

  // A blank, unlit door while the calendar counts down; otherwise a live
  // miniature of today's tile, or the opened count once the run is over.
  const doorFace = () => {
    if (isCountdown) return null;
    if (isDoorOpened && Icon) {
      return (
        <>
          <span className={styles["door-small-number"]}>
            {doorForToday?.dayNumber}
          </span>
          <Icon className={styles["door-icon"]} />
        </>
      );
    }
    return (
      <span className={styles["door-number"]}>
        {doorForToday ? doorForToday.dayNumber : campaign.openedCount}
      </span>
    );
  };

  return (
    <div className={styles.band}>
      <button
        ref={ref}
        type="button"
        className={styles.action}
        onClick={handleClick}
        data-testid="december-doors-band"
      >
        <span className={styles.inner}>
          <span
            className={clsx(
              styles.door,
              isDoorOpened && styles["door-opened"],
              isCountdown && styles["door-waiting"]
            )}
            aria-hidden
          >
            {doorFace()}
          </span>
          <span className={styles.body}>
            <span className={styles.title}>
              <span className={styles["ori-mark"]} aria-hidden>
                <OriAvatar size={20} cropScale={ORI_ORB_CROP_SCALE} />
              </span>
              {title()}
            </span>
            <span
              className={styles.status}
              data-testid="december-doors-band-status"
            >
              {status()}
            </span>
          </span>
          <span className={styles.cta}>
            {readyDay === null
              ? t("decemberDoors:card.openCalendar")
              : t("decemberDoors:card.open", { day: readyDay })}
          </span>
        </span>
      </button>
    </div>
  );
};

export default DecemberDoorsBand;
